import React, { useEffect, useState } from "react"
import { Card, CardBody, CardTitle, Button, Spinner } from "reactstrap"
import { Formik, Form, Field, ErrorMessage } from "formik"
import { addCategoriesSchemas } from "components/Common/validation"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useLocation, useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import Switch from "components/Shared/Switch"
import { useDispatch } from "react-redux"
import {
  addCategory,
  getCategoryDetails,
  updateCategoryDetails,
} from "store/actions/categoriesAction"
import Spinners from "components/Shared/Spinners"
import { showAlert } from "store/reducers/alertReducer"
import BackButton from "components/Shared/BackButton"
import {
  getImagePreviewSrc,
  handleFileChange,
} from "components/Shared/fileHandlers"
import Select from "react-select"
import { getCountryList } from "store/actions/countriesAction"
import { useTranslation } from "react-i18next"
import {
  categoryHeight,
  categoryMaxSize,
  categoryWidth,
} from "components/Common/ImageSize"
import TextError from "components/Shared/TextError"

const initialValues = {
  nameAr: "",
  nameEn: "",
  descriptionAr: "",
  descriptionEn: "",
  displayOrder: null,
  imageType: "UPLOAD",
  imageUrl: "",
  isCombo: false,
  imageFile: null,
  countryCode: null,
}

const AddCategory = ({ mode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = `${mode === "add" ? t("Add") : t("Edit")} ${t(
    "Category | Namq"
  )}`

  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")
  const dispatch = useDispatch()
  const { loading, isLoading, categoryDetails } = useSelector(
    state => state.Categories
  )
  const { countryList } = useSelector(state => state.Countries)
  const { isImageCompressing } = useSelector(state => state.Common)
  const [formValues, setFormValues] = useState(initialValues)

  // IF HAVE MODE===EDIT THEN UPDATE INITAIL VALUES
  useEffect(() => {
    if (mode === "edit") {
      getOldValues()
    }
    fetchCountryList()
  }, [])

  const getOldValues = () => {
    const payload = {
      id,
    }
    dispatch(getCategoryDetails(payload))
  }

  const fetchCountryList = () => {
    dispatch(getCountryList())
  }
  // GET OLD VALUES WHEN HAVE
  useEffect(() => {
    if (categoryDetails && !loading && mode === "edit") {
      const {
        _id,
        name,
        nameAr,
        description,
        descriptionAr,
        imageUrlType,
        imageUrl,
        displayOrder,
        disabledBranches,
        status,
        isCombo,
        countryCode,
      } = categoryDetails

      const matchedCountry = countryList.find(
        item => item.countryCode === countryCode
      )
      var updatedCountryCode = null
      if (matchedCountry) {
        const { name, countryCode } = matchedCountry
        updatedCountryCode = { label: name, value: countryCode }
      }

      setFormValues({
        ...formValues,
        nameAr,
        nameEn: name,
        descriptionAr,
        descriptionEn: description,
        displayOrder,
        imageType: imageUrlType,
        imageUrl,
        isCombo: isCombo,
        countryCode: updatedCountryCode,
      })
    }
  }, [categoryDetails, countryList])

  const handleSubmit = values => {
    const {
      nameAr,
      nameEn,
      descriptionAr,
      descriptionEn,
      displayOrder,
      imageType,
      imageUrl,
      isCombo,
      imageFile,
      countryCode,
    } = values

    const data = new FormData()
    data.append("name", nameEn)
    data.append("nameAr", nameAr)
    data.append("description", descriptionEn)
    data.append("descriptionAr", descriptionAr)
    data.append("displayOrder", displayOrder)
    // data.append("disabledBranches[0]", "")
    data.append("imageUrl", imageUrl)
    data.append("imageUrlType", imageType)
    data.append("isCombo", isCombo)
    data.append("countryCode", countryCode?.value?.toUpperCase())
    data.append("image", imageFile)
    if (mode === "add") {
      dispatch(addCategory(data))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(-1)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    } else if (mode === "edit") {
      data.append("id", id)
      dispatch(updateCategoryDetails(data))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(-1)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    }
  }

  if (mode === "edit" && loading) return <Spinners />

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />

        <Card>
          <CardBody>
            <CardTitle className="d-flex align-items-center">
              <BackButton />
              {mode === "add" ? t("Add") : t("Edit")} {t("Category")}
            </CardTitle>
            <Formik
              enableReinitialize={true}
              initialValues={formValues}
              onSubmit={handleSubmit}
              validationSchema={addCategoriesSchemas(mode)}
            >
              {({ errors, touched, values, isSubmitting, setFieldValue }) => (
                <Form>
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="nameEn">
                          <span className="text-danger">*</span>{" "}
                          {t("Name(English)")}
                        </label>
                        <Field
                          type="text"
                          id="nameEn"
                          name="nameEn"
                          placeholder={t("Name(English)")}
                          className={`form-control ${
                            touched.nameEn && errors.nameEn ? "is-invalid" : ""
                          }`}
                        />
                        <TextError name="nameEn" />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="nameAr">
                          <span className="text-danger">*</span>{" "}
                          {t("Name(عربي)")}
                        </label>
                        <Field
                          type="text"
                          style={{ direction: "rtl" }}
                          id="nameAr"
                          name="nameAr"
                          placeholder={t("Name(عربي)")}
                          className={`form-control ${
                            touched.nameAr && errors.nameAr ? "is-invalid" : ""
                          }`}
                        />
                        <TextError name="nameAr" />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="descriptionEn">
                          {t("Description(English)")}
                        </label>
                        <Field
                          as="textarea"
                          rows={1}
                          id="descriptionEn"
                          name="descriptionEn"
                          placeholder={t("Description(English)")}
                          className={`form-control ${
                            touched.descriptionEn && errors.descriptionEn
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="descriptionAr">
                          {t("Description(عربي)")}
                        </label>
                        <Field
                          as="textarea"
                          rows={1}
                          style={{ direction: "rtl" }}
                          id="descriptionAr"
                          name="descriptionAr"
                          placeholder={t("Description(عربي)")}
                          className={`form-control ${
                            touched.descriptionAr && errors.descriptionAr
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <TextError name="descriptionAr" />
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="displayOrder">
                          <span className="text-danger">*</span>{" "}
                          {t("Display Order")}
                        </label>
                        <Field
                          type="text"
                          id="displayOrder"
                          name="displayOrder"
                          placeholder={t("Display Order")}
                          className={`form-control ${
                            touched.displayOrder && errors.displayOrder
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <TextError name="displayOrder" />
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="mt-2">
                        <label htmlFor="countryCode">
                          <span className="text-danger">*</span>{" "}
                          {t("Select Country")}
                        </label>
                        <Select
                          value={values.countryCode}
                          onChange={selectedValue => {
                            setFieldValue("countryCode", selectedValue)
                          }}
                          options={countryList?.map(item => ({
                            label: language === "sa" ? item.nameAr : item.name,
                            value: item.countryCode,
                          }))}
                          placeholder={t("Select Country")}
                          className={`select2-selection ${
                            touched.countryCode && errors.countryCode
                              ? "danger_color_border"
                              : ""
                          } `}
                        />
                        <TextError name="countryCode" />
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="isCombo">{t("Is Combo")}</label>
                        <Switch
                          isChecked={values.isCombo}
                          onClickHandler={() => {
                            setFieldValue("isCombo", !values.isCombo)
                          }}
                        />
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="imageType">
                          <span className="text-danger">*</span>{" "}
                          {t("Image Type")}
                        </label>
                        <Field
                          as="select"
                          id="imageType"
                          name="imageType"
                          className={`form-control ${
                            touched.imageType && errors.imageType
                              ? "is-invalid"
                              : ""
                          }`}
                          onChange={e => {
                            const selectedValue = e.target.value
                            setFieldValue("imageType", selectedValue)
                            // Reset the value of imageUrl and imageFile when the image type changes
                            setFieldValue("imageUrl", "")
                            setFieldValue("imageFile", null)
                          }}
                        >
                          <option value="URL">{t("Image Url")}</option>
                          <option value="UPLOAD">{t("Upload Image")}</option>
                        </Field>
                        <TextError name="imageType" />
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form_field">
                        {/* IMAGE URL */}
                        {values.imageType === "URL" && (
                          <div className="form_field">
                            <label htmlFor="imageUrl">
                              {" "}
                              <span className="text-danger">*</span>{" "}
                              {t("Image URL")}
                            </label>
                            <Field
                              type="text"
                              id="imageUrl"
                              name="imageUrl"
                              className={`form-control ${
                                touched.imageUrl && errors.imageUrl
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError
                              name="imageUrl"
                              component="div"
                              className="text-danger"
                            />
                          </div>
                        )}
                        {/* UPLOAD IMAGE */}
                        {values.imageType === "UPLOAD" && (
                          <div className="form_field position-relative">
                            <label htmlFor="imageFile">
                              <span className="text-danger">*</span>{" "}
                              {t("Upload Image")}
                            </label>
                            <input
                              type="file"
                              id="imageFile"
                              name="imageFile"
                              accept="image/jpeg, image/jpg, image/png"
                              onChange={e => {
                                handleFileChange(
                                  e,
                                  "imageFile",
                                  setFieldValue,
                                  categoryWidth,
                                  categoryHeight,
                                  categoryMaxSize
                                )
                              }}
                              className={`form-control ${
                                touched.imageFile && errors.imageFile
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError
                              name="imageFile"
                              component="div"
                              className="text-danger"
                            />
                            {isImageCompressing && (
                              <Spinner
                                color="primary"
                                className="file_upload_btn"
                              />
                            )}
                          </div>
                        )}
                        <span>
                          {t(
                            "Recommended dimensions: Square Image and Size should be less than or equal to {{size}} KB",
                            {
                              width: categoryWidth,
                              height: categoryHeight,
                              size: categoryMaxSize,
                            }
                          )}
                        </span>
                        {getImagePreviewSrc(mode, values) && (
                          <div className="mt-3">
                            <img
                              src={getImagePreviewSrc(mode, values)}
                              alt="Preview"
                              className="img_preview"
                            />
                          </div>
                        )}

                        {/* {mode === "edit" && !values.imageFile ? (
                          <img
                            src={values.imageUrl}
                            alt="Preview"
                            className="img_preview"
                          />
                        ) : (
                          values.imageFile && (
                            <div className="mt-3">
                              <img
                                src={URL.createObjectURL(values.imageFile)}
                                alt="Preview"
                                className="img_preview"
                              />
                            </div>
                          )
                        )} */}
                      </div>
                    </div>

                    <div className="mt-3"></div>
                    <div className="col-12 d-flex justify-content-end">
                      <Button
                        type="button"
                        className="mx-2"
                        color="secondary"
                        onClick={() => navigate(-1)}
                      >
                        {t("Cancel")}
                      </Button>
                      <Button
                        type="submit"
                        color="success"
                        disabled={isLoading || isImageCompressing}
                        className="w_fit_content"
                      >
                        {mode === "add" ? t("Add") : t("Save")}
                      </Button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </CardBody>
        </Card>
      </div>
    </div>
  )
}

export default AddCategory
